import { Button, useToast } from '@hkdevs/design-system';
import { useState } from 'react';
import {
  useDownloadOriginal,
  usePendingSummary,
  type PendingPaySummary,
  type UploadSummary,
} from '../../api/queries/uploads';
import { PageLayout } from '../../shell/Page';
import { downloadBlob } from '../payHistory/csv';
import { PendingPayUploadsView, type PayDataPendingSearch } from './PendingPayUploadsView';
import { PendingUploadDrawer } from './PendingUploadDrawer';
import {
  ClearPendingModal,
  DeleteUploadModal,
  UploadErrorReportModal,
  type UploadRef,
} from './UploadModals';
import { withReference } from '../../api/withReference';

/**
 * Pending pay uploads (pc-50i.4): the header's Upload files over the pending summary and
 * uploads, with the three-step upload drawer, Clear pending pay, a rejected upload's
 * error report and Delete upload.
 */
export function PendingPayUploadsPage({
  search,
  onSearchChange,
}: {
  search: PayDataPendingSearch;
  onSearchChange: (next: PayDataPendingSearch) => void;
}) {
  const { toast } = useToast();
  const summary = usePendingSummary();
  // A new key each time the drawer opens, so it starts at step 1.
  const [drawerKey, setDrawerKey] = useState<number | null>(null);
  const [clearing, setClearing] = useState<PendingPaySummary | null>(null);
  const [report, setReport] = useState<UploadRef | null>(null);
  const [deleting, setDeleting] = useState<UploadRef | null>(null);
  const original = useDownloadOriginal();

  const download = (upload: UploadSummary) =>
    original.mutate(upload, {
      onSuccess: ({ blob, fileName }) => downloadBlob(blob, fileName),
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: `${upload.fileName} couldn’t be downloaded`,
              description: 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });

  const lockedDate = summary.data?.active ? summary.data.payDate : null;
  return (
    <PageLayout
      page="payDataPending"
      actions={
        <Button
          size="sm"
          leadingIcon="upload"
          onClick={() => setDrawerKey(Date.now())}
          disabled={!summary.data}
        >
          Upload files
        </Button>
      }
    >
      <PendingPayUploadsView
        search={search}
        onSearchChange={onSearchChange}
        onClearPending={setClearing}
        onDownload={download}
        onOpenErrorReport={setReport}
        onDelete={setDeleting}
        announce={drawerKey === null}
      />
      {drawerKey !== null && (
        <PendingUploadDrawer
          key={drawerKey}
          open
          onClose={() => setDrawerKey(null)}
          lockedDate={lockedDate}
          onOpenErrorReport={setReport}
        />
      )}
      {clearing && <ClearPendingModal summary={clearing} onClose={() => setClearing(null)} />}
      {report && <UploadErrorReportModal target={report} onClose={() => setReport(null)} />}
      {deleting && <DeleteUploadModal target={deleting} onClose={() => setDeleting(null)} />}
    </PageLayout>
  );
}
